import { GetServerSideProps, type NextPage } from 'next'
import dynamic from 'next/dynamic'
import { unstable_serialize } from 'swr'
import { client } from '~/api/client'
import {
  Schemas,
  getItemById,
  getProductsSearchV1,
  getItemsItemIdRelatedItemsV1,
} from '~/api/gen'
import { Layout } from '~/components/Layout'
import { Meta } from '~/components/Meta'
import { ItemDetail } from '~/features/ItemDetail/ItemDetail'
import { getMetaData } from '~/features/ItemDetail/lib/getData'
import { getBreadcrumbList } from '~/features/shared/item/lib/getBreadcrumbList'
import { fetchSearchCoordinates } from '~/features/shared/search/utils/fetchSearchCoordinates'
import { getSearchCoordinatesKey } from '~/features/shared/search/utils/getSearchCoordinatesKey'
import { ITEM_DETAIL_HEADER_ADJUST_URL } from '~/lib/constants'
import { sexToGender } from '~/lib/gender/converter'

const GoogleAdScripts = dynamic(
  () => import('~/features/shared/ad/components/GoogleAdScripts'),
  {
    ssr: false,
  }
)

class ValidationError extends Error {}

export type Props = {
  item: Schemas.item_large
  relatedCoordinateCount: number
  relatedProducts: Schemas.get_product_with_members_response
  relatedItems: Schemas.item_collection_with_purchase_items[]
}

export const ItemDetailPage: NextPage<Props> = ({
  item,
  relatedCoordinateCount,
  relatedProducts,
  relatedItems,
}) => {
  return (
    <Layout
      breadcrumbList={getBreadcrumbList({
        brand: item.brand?.id
          ? {
              id: item.brand.id,
              name: item.brand.name,
            }
          : undefined,
        category: item.category?.id
          ? {
              id: item.category.id,
              name: item.category.name,
            }
          : undefined,
        childCategory: item.category.child_category?.id
          ? {
              id: item.category.child_category.id,
              name: item.category.child_category.name,
            }
          : undefined,
        name: item.name,
        gender: sexToGender(item.sex_id),
      })}
      headerProps={{ hideAppIcon: true }}
      appUrl={`wear2020://wear.jp/item/${item.id}/`}
      appDownloadUrl={ITEM_DETAIL_HEADER_ADJUST_URL}
      abstractUrl="wear.jp/item/itemid/"
    >
      <Meta {...getMetaData(item, relatedItems)} />
      <GoogleAdScripts
        pages={[{ label: 'sp:item:detail' }, { label: 'pc:item:detail' }]}
      />
      <ItemDetail
        item={item}
        relatedCoordinateCount={relatedCoordinateCount}
        relatedProducts={relatedProducts}
        relatedItems={relatedItems}
      />
    </Layout>
  )
}

const PAGE_NO = 1
const PAGE_SIZE = 9

/* eslint-disable complexity */
export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
}) => {
  const itemId = Number(
    typeof query.itemId === 'string' ? query.itemId : query.itemId?.[0]
  )
  const isValid = Number.isInteger(itemId)
  if (!isValid || itemId <= 0) {
    throw new ValidationError('Unexpected itemId')
  }

  const coordinateSearchKey = getSearchCoordinatesKey({
    item_id: itemId,
    pageno: PAGE_NO,
    pagesize: PAGE_SIZE,
  })

  const [itemResponse, coordinateResponse, relatedItemResponse] =
    await Promise.allSettled([
      getItemById(client, {
        parameter: { item_id: itemId },
      }),
      fetchSearchCoordinates(coordinateSearchKey),
      getItemsItemIdRelatedItemsV1(client, {
        parameter: { item_id: itemId, count: 5 },
      }),
    ])

  //アイテム
  if (itemResponse.status === 'rejected')
    throw new Error(`Failed to fetch item: ${itemResponse.reason}`)
  if (itemResponse.value.raw.status === 404) return { notFound: true }
  if (itemResponse.value.type === 'error')
    throw new Error(
      `Failed to fetch item: ${itemResponse.value.raw.statusText}`
    )
  const item = itemResponse.value.data.item

  //着用コーディネート
  const coordinates: Schemas.get_coordinates_search_v2_response =
    coordinateResponse.status === 'fulfilled'
      ? coordinateResponse.value
      : { coordinates: [], total_count: 0, max_count: 100000, request_time: '' }

  //関連アイテム
  const relatedItems =
    relatedItemResponse.status === 'fulfilled' &&
    relatedItemResponse.value.type === 'success'
      ? relatedItemResponse.value.data.items
      : []

  //フリマ関連アイテム
  let relatedProducts: Schemas.get_product_with_members_response = {
    products: [],
    next_cursor: null,
    total_count: 0,
  }

  // フリマアイテム一覧のAPIのレスポンスが遅いことがある。ページ全体のレイテンシーに影響しないようにタイムアウト短くしておく。
  try {
    const productsResponse = await getProductsSearchV1(
      client,
      {
        parameter: {
          sex_id:
            item.sex_id === 1 || item.sex_id === 2 || item.sex_id === 3
              ? item.sex_id
              : undefined,
          item_category_id: item.category.id ?? undefined,
          item_child_category_id: item.category.child_category.id ?? undefined,
          sort: 'followers',
          ppf_sell_statuses: 'open',
          'x-wr-time-zone': 'Asia/Tokyo',
          count: 10,
        },
      },
      { timeout: 1500 }
    )
    if (productsResponse.type === 'success')
      relatedProducts = productsResponse.data
  } catch (err) {
    console.error(err)
  }

  return {
    props: {
      fallback: {
        [unstable_serialize(coordinateSearchKey)]: coordinates,
      },
      item,
      relatedCoordinateCount: coordinates.total_count,
      relatedProducts,
      relatedItems,
    },
  }
}
export default ItemDetailPage
